// M004-F0647 — the weekly coverage strip. Draws ONLY what Core's coverage
// read says (`GET .../members/:userId/coverage`): the UI does NO rate
// arithmetic, it never decides which plan wins - `slot.winner` does.
//
// Accessibility (design §3.3): role=grid with a roving tabindex, arrow keys
// move, Enter pins; every cell's aria-label carries the inspector sentence.
import React, { useMemo, useState } from 'react';
import { DAY_LABELS, PATTERN_CSS, formatPerHour, styleForPlan } from './planModel';

const minutesOf = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + m;
};

// Shown by default: 06:00-24:00. Overnight rows sit behind a toggle.
const DEFAULT_FROM_MIN = 6 * 60;

export const slotKey = (day, start) => `${day}|${start}`;

const indexCoverage = (coverage) => {
  const byDay = new Map();
  const times = new Set();
  (coverage?.days || []).forEach((day) => {
    const slots = new Map();
    (day.slots || []).forEach((slot) => {
      slots.set(slot.start, slot);
      times.add(slot.start);
    });
    byDay.set(Number(day.iso_weekday), slots);
  });
  return { byDay, times: [...times].sort((a, b) => minutesOf(a) - minutesOf(b)) };
};

export const planNameIn = (coverage, planId) => (
  (coverage?.plans || []).find((plan) => Number(plan.id) === Number(planId))?.name || `Plan ${planId}`
);

/** The sentence the inspector and aria-labels carry (design §3.3). */
export const describeSlot = (coverage, isoWeekday, slot) => {
  if (!slot || !slot.winner) return `${DAY_LABELS[isoWeekday - 1]}: no price`;
  const winnerName = planNameIn(coverage, slot.winner.plan_id);
  return `${DAY_LABELS[isoWeekday - 1]} ${slot.start} → ${formatPerHour(slot.winner.amount_minor)} via ${winnerName} (cheapest of ${slot.applicable_count} applicable)`;
};

export const changedSlotKeys = (before, after) => {
  const a = indexCoverage(before);
  const b = indexCoverage(after);
  const changed = new Set();
  b.byDay.forEach((slots, day) => {
    slots.forEach((slot, start) => {
      const prev = a.byDay.get(day)?.get(start);
      if (!prev || !prev.winner || !slot.winner
        || prev.winner.plan_id !== slot.winner.plan_id
        || prev.winner.amount_minor !== slot.winner.amount_minor) {
        changed.add(slotKey(day, start));
      }
    });
  });
  return changed;
};

const Cell = ({
  coverage, styles, day, slot, start, focused, pinned, changed, onHover, onFocusCell, onPin, tabIndexValue, cellRef,
}) => {
  const winnerStyle = slot?.winner ? styleForPlan(styles, slot.winner.plan_id) : null;
  const pattern = winnerStyle ? PATTERN_CSS[winnerStyle.pattern] : 'none';
  const label = describeSlot(coverage, day, slot);
  return (
    <div
      role="gridcell"
      ref={cellRef}
      tabIndex={tabIndexValue}
      aria-label={label}
      aria-selected={pinned ? 'true' : undefined}
      data-testid="coverage-cell"
      data-day={day}
      data-start={start}
      data-plan-id={slot?.winner?.plan_id ?? ''}
      data-amount-minor={slot?.winner?.amount_minor ?? ''}
      data-multi={slot && slot.applicable_count > 1 ? 'true' : 'false'}
      data-changed={changed ? 'true' : 'false'}
      className={[
        'mpp-cell',
        slot && slot.applicable_count > 1 ? 'mpp-cell-multi' : '',
        focused ? 'mpp-cell-focus' : '',
        pinned ? 'mpp-cell-pinned' : '',
        changed ? 'mpp-cell-changed' : '',
      ].filter(Boolean).join(' ')}
      style={winnerStyle ? { backgroundColor: winnerStyle.color, backgroundImage: pattern === 'none' ? undefined : pattern } : undefined}
      onMouseEnter={() => onHover(day, start)}
      onFocus={() => onFocusCell(day, start)}
      onClick={() => onPin(day, start)}
    />
  );
};

const Strip = ({
  coverage, styles, caption, changedKeys, showOvernight, active, focusTick, keyboard, pinned, onHover, onFocusCell, onPin, onKeyDown, testId,
}) => {
  const { byDay, times } = useMemo(() => indexCoverage(coverage), [coverage]);
  const rows = times.filter((t) => showOvernight || minutesOf(t) >= DEFAULT_FROM_MIN);
  const refs = React.useRef(new Map());
  React.useEffect(() => {
    if (keyboard && focusTick > 0) {
      const node = refs.current.get(slotKey(active.day, active.start));
      if (node) node.focus();
    }
    // focus only when an arrow key asked for it (focusTick), not on hover
  }, [focusTick]);
  return (
    <div className="mpp-stripwrap">
      {caption ? <div className="mpp-strip-caption">{caption}</div> : null}
      <div
        className="mpp-strip"
        role="grid"
        aria-label={`${caption ? `${caption}. ` : ''}Weekly price coverage. Use arrow keys to inspect a slot; Enter pins it.`}
        data-testid={testId}
        onKeyDown={onKeyDown}
      >
        <div role="row" className="mpp-strip-head">
          <div role="columnheader" className="mpp-strip-corner" />
          {DAY_LABELS.map((label) => (
            <div role="columnheader" className="mpp-strip-dayhead" key={label}>{label}</div>
          ))}
        </div>
        {rows.map((start, rowIndex) => (
          <div role="row" className="mpp-strip-row" key={start}>
            <div role="rowheader" className="mpp-strip-time">{rowIndex % 2 === 0 ? start : ''}</div>
            {DAY_LABELS.map((label, dayIndex) => {
              const day = dayIndex + 1;
              const slot = byDay.get(day)?.get(start) || null;
              const isActive = keyboard && active.day === day && active.start === start;
              return (
                <Cell
                  key={label}
                  coverage={coverage}
                  styles={styles}
                  day={day}
                  start={start}
                  slot={slot}
                  focused={Boolean(isActive)}
                  pinned={Boolean(pinned && pinned.day === day && pinned.start === start)}
                  changed={changedKeys ? changedKeys.has(slotKey(day, start)) : false}
                  tabIndexValue={isActive ? 0 : -1}
                  cellRef={(node) => {
                    if (node) refs.current.set(slotKey(day, start), node);
                    else refs.current.delete(slotKey(day, start));
                  }}
                  onHover={onHover}
                  onFocusCell={onFocusCell}
                  onPin={onPin}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const CoverageStrip = ({
  coverage, afterCoverage = null, styles, loading = false, error = '',
}) => {
  const [showOvernight, setShowOvernight] = useState(false);
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [active, setActive] = useState({ day: 6, start: '10:00' });
  const [focusTick, setFocusTick] = useState(0);

  const { times } = useMemo(() => indexCoverage(coverage), [coverage]);
  const visibleTimes = times.filter((t) => showOvernight || minutesOf(t) >= DEFAULT_FROM_MIN);
  const changedKeys = useMemo(
    () => (afterCoverage ? changedSlotKeys(coverage, afterCoverage) : null),
    [coverage, afterCoverage],
  );

  if (error) {
    return <div className="mpp-error" role="alert" data-testid="coverage-error">{error}</div>;
  }
  if (!coverage) {
    return <div className="mpp-muted" data-testid="coverage-loading">{loading ? 'Loading weekly coverage…' : 'No coverage to show.'}</div>;
  }

  const focusKey = pinned || hover || active;
  const inspectCoverage = afterCoverage && focusKey && focusKey.which === 'after' ? afterCoverage : coverage;
  const slot = focusKey
    ? indexCoverage(inspectCoverage).byDay.get(focusKey.day)?.get(focusKey.start)
    : null;

  const move = (event) => {
    const deltas = {
      ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
    };
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setPinned({ day: active.day, start: active.start });
      return;
    }
    if (event.key === 'Escape') {
      setPinned(null);
      return;
    }
    const delta = deltas[event.key];
    if (!delta) return;
    event.preventDefault();
    const day = Math.min(7, Math.max(1, active.day + delta[0]));
    const index = Math.max(0, visibleTimes.indexOf(active.start));
    const next = visibleTimes[Math.min(visibleTimes.length - 1, Math.max(0, index + delta[1]))];
    setActive({ day, start: next });
    setFocusTick((tick) => tick + 1);
    if (!pinned) setHover(null);
  };

  const handlers = (which) => ({
    onHover: (day, start) => { if (!pinned) setHover({ day, start, which }); },
    onFocusCell: (day, start) => { setActive({ day, start }); },
    onPin: (day, start) => { setPinned({ day, start, which }); setActive({ day, start }); },
  });

  return (
    <div className="mpp-coverage" data-testid="price-plans-coverage-strip">
      <div className="mpp-row mpp-strip-tools">
        <button
          type="button"
          className="mpp-linkbtn"
          aria-pressed={showOvernight}
          onClick={() => setShowOvernight((v) => !v)}
          data-testid="coverage-overnight-toggle"
        >
          {showOvernight ? 'Hide overnight (00:00–06:00)' : 'Show overnight (00:00–06:00)'}
        </button>
      </div>
      {afterCoverage ? (
        <>
          <Strip
            coverage={coverage}
            styles={styles}
            caption="Now"
            changedKeys={null}
            showOvernight={showOvernight}
            active={active}
            focusTick={focusTick}
            keyboard={false}
            pinned={pinned}
            onKeyDown={move}
            testId="coverage-strip-now"
            {...handlers('now')}
          />
          <Strip
            coverage={afterCoverage}
            styles={styles}
            caption="After"
            changedKeys={changedKeys}
            showOvernight={showOvernight}
            active={active}
            focusTick={focusTick}
            keyboard
            pinned={pinned}
            onKeyDown={move}
            testId="coverage-strip-after"
            {...handlers('after')}
          />
          <p className="mpp-strip-changes" data-testid="coverage-change-count" aria-live="polite">
            {changedKeys.size === 0
              ? 'No slots change price.'
              : `${changedKeys.size} slot${changedKeys.size === 1 ? '' : 's'} change price`}
          </p>
        </>
      ) : (
        <Strip
          coverage={coverage}
          styles={styles}
          caption=""
          changedKeys={null}
          showOvernight={showOvernight}
          active={active}
          focusTick={focusTick}
          keyboard
          pinned={pinned}
          onKeyDown={move}
          testId="coverage-strip"
          {...handlers('now')}
        />
      )}
      <div className="mpp-legend" data-testid="coverage-legend">
        {(coverage.plans || []).map((plan) => {
          const st = styleForPlan(styles, plan.id);
          return (
            <span key={plan.id} data-plan-id={plan.id}>
              <i style={{ backgroundColor: st.color, backgroundImage: st.pattern === 'solid' ? undefined : PATTERN_CSS[st.pattern] }} />
              {plan.name}
            </span>
          );
        })}
        <span><i className="mpp-legend-multi" />{'◥'} more than one plan applies</span>
      </div>
      <div className="mpp-inspector" aria-live="polite" data-testid="coverage-inspector">
        {slot && slot.winner ? (
          <>
            <b data-testid="coverage-inspector-line">{describeSlot(inspectCoverage, focusKey.day, slot)}</b>
            {slot.applicable_count > 1 ? (
              <ul>
                {[...(slot.applicable || [])]
                  .sort((x, y) => x.amount_minor - y.amount_minor || x.plan_id - y.plan_id)
                  .map((entry) => (
                    <li key={entry.plan_id}>
                      {planNameIn(inspectCoverage, entry.plan_id)} {formatPerHour(entry.amount_minor)}
                      {entry.plan_id === slot.winner.plan_id ? ' — wins' : ' — dearer'}
                    </li>
                  ))}
              </ul>
            ) : null}
          </>
        ) : 'Hover, tap or use arrow keys on a slot to see its price.'}
      </div>
      <p className="mpp-footnote">
        Shown for a {coverage.probe_minutes || 60}-minute booking; longer bookings can price differently.
        A corner tick marks a slot where more than one plan applies. Computed by Core, the same selector as real quotes.
      </p>
    </div>
  );
};

export default CoverageStrip;
